import { useVehicleTypeOptions } from "hooks/useVehicleTypeOptions";
import { AccountDeliveryDriverType } from "../types/AccountDeliveryDriverType";
import { t } from '../../../../i18n';
import { Form, FormInstance, Alert, Typography, Modal } from "antd";
import { countryCodeOptions } from "constants/countryCodes";
import { Select } from "antd";
import { Input } from "antd";
import { passwordPolicyRule } from 'utils/passwordValidator';
import ImageUploader from "components/ImageUploader";
import { formatDate } from "utils/dateTimeUtils";
import EditableDrawer from "components/EditableDrawer";
import { useState } from "react";
import { useEffect } from "react";
import { useRef } from "react";
import { Divider } from "antd";
import { CountryCodeAndPhoneNumber, PhoneInput } from "components/PhoneInput";
import { AccountsPageMode, ACCOUNTS_PAGE_MODE } from "../hooks/useAccountDeliveryDriverQuery";
import { checkDeliveryDriverUniqueness } from "../graphql/queries/checkDeliveryDriverUniqueness";
import {
    buildPhoneCompareValue,
    createPointerReleaseWaiter,
    createUniquenessCheckGuard,
    DriverUniquenessField,
    parseDriverConflictDetails,
    shouldCheckDriverUniqueness,
    UniquenessCheckGuard,
} from "utils/driverConflictMessage";
import { DriverConflict } from "./DriverConflictModal";

interface AccountFormProps {
    mode: 'create' | 'edit';
    accountDeliveryDriver?: AccountDeliveryDriverType | null;
    form: FormInstance<AccountDeliveryDriverType>;
    source?: AccountsPageMode; // 页面入口：central/wms/dsp，central、wms 可选择 Company/DSP
    dspOptions?: Array<{ label: string; value: string; dspCode: string }>;
    dspLoading?: boolean;
    onUniquenessConflict?: (conflict: DriverConflict) => void;
    uniquenessCheckGuard: UniquenessCheckGuard;
    waitForPointerRelease: () => Promise<void>;
}

const AccountDeliveryDriverForm = ({
    mode, form, accountDeliveryDriver, source, dspOptions = [], dspLoading = false, onUniquenessConflict, uniquenessCheckGuard, waitForPointerRelease,
}: AccountFormProps) => {

    const vehicleTypeOptions = useVehicleTypeOptions();
    const showDspSelector = source === ACCOUNTS_PAGE_MODE.WMS || source === ACCOUNTS_PAGE_MODE.CENTRAL;
    // 监听 isEmployed 字段的变化
    const isEmployed = Form.useWatch(['account', 'isEmployed'], form) ?? true;
    // 编辑模式下，如果选择离职，则其他字段非必填（除了电话号）
    const isRequired = !(mode === 'edit' && isEmployed === false);
    // 仅当是编辑模式并且初始状态就是离职或封禁（用于展示离职/封禁司机详情时），输入框全部禁用
    const isDisabled = mode === 'edit' && (accountDeliveryDriver?.isEmployed === false || accountDeliveryDriver?.isBlacklist === true);

    // 当选择离职时，清除其他字段的校验错误（保留电话号的校验）
    useEffect(() => {
        if (mode === 'edit' && isEmployed === false) {
            // 清除除电话号外的所有字段的校验错误
            const fieldsToClear: Array<string | string[]> = [
                ['account', 'name'],
                ['account', 'email'],
                'dspId',
                'idDocumentType',
                'idDocumentNumber',
                'idDocumentPhotos',
                'vehicleTypeId',
                'vehiclePlate',
                'vehiclePhotos',
            ];
            
            // 使用 setFields 清除校验错误
            const fields = fieldsToClear.map(fieldName => ({
                name: fieldName as any,
                errors: [],
            }));
            form.setFields(fields);
        }
    }, [isEmployed, mode, form]);

    const handleChange = (name: string, value: any) => {
        form.setFieldsValue({
            [name]: value
        });
    };

    // 手机号 / 证件号失焦即时校验唯一性：聚焦时记录值，失焦时值有变动且格式通过才查询，冲突弹窗与提交时一致；
    // 未改动不重复查询（如焦点在输入框时点取消 / 关闭），提交时后端仍会校验
    const focusValueRef = useRef<{ phone?: string; idDocumentNumber?: string }>({});
    const getPhoneData = () => form.getFieldValue('phoneData' as any) as CountryCodeAndPhoneNumber | undefined;
    const getPhoneCompareValue = () => buildPhoneCompareValue(getPhoneData()?.countryCode, getPhoneData()?.phoneNumber);
    const getIdDocumentNumber = () => form.getFieldValue('idDocumentNumber') as string | undefined;

    /**
     * @param value 查询参数（手机号只传号码，后端查重不区分区号）
     * @param compareValue / originalCompareValue 判断是否变动用的比较值（手机号含区号）
     */
    const checkUniquenessOnBlur = async (
        field: DriverUniquenessField,
        formField: string,
        value: string | undefined,
        compareValue: string | undefined,
        originalCompareValue: string | null | undefined,
    ) => {
        const shouldCheck = shouldCheckDriverUniqueness({
            value: compareValue,
            originalValue: originalCompareValue,
            focusValue: focusValueRef.current[field],
            isResigning: mode === 'edit' && isEmployed === false,
        });
        if (!shouldCheck) {
            return;
        }
        const token = uniquenessCheckGuard.start(field);
        // 鼠标点击导致的失焦，等点击处理完再查：点确认 / 取消 / 关闭 / 遮罩时查询已作废，不再发请求
        await waitForPointerRelease();
        if (!uniquenessCheckGuard.isLatest(field, token)) {
            return;
        }
        try {
            await form.validateFields([formField]);
        } catch {
            // 格式校验未通过，交由表单提示
            return;
        }
        try {
            const conflict = await checkDeliveryDriverUniqueness({
                [field]: value!.trim(),
                excludeAccountId: mode === 'edit' ? accountDeliveryDriver?.accountId : null,
            });
            if (conflict && uniquenessCheckGuard.isLatest(field, token)) {
                onUniquenessConflict?.({ messageKey: conflict.messageKey, details: parseDriverConflictDetails(conflict.details) });
            }
        } catch (e) {
            // 查询失败不拦截，提交时后端仍会校验
            console.warn('checkDeliveryDriverUniqueness failed:', e);
        }
    };

    return (
        <Form
            form={form}
            labelAlign="left"
            labelCol={{ span: 6, style: { whiteSpace: 'normal', lineHeight: '1.4' } }}
            wrapperCol={{ span: 18 }}
        >
            <Form.Item name={['account', 'id']} hidden>
            </Form.Item>

            {/* 区号下拉与号码输入框之间切换焦点不算进出，焦点进入时记录值、离开整个手机号区域才校验 */}
            <div
                onFocus={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                        focusValueRef.current.phone = getPhoneCompareValue();
                    }
                }}
                onBlur={(e) => {
                    if (e.currentTarget.contains(e.relatedTarget as Node | null)) {
                        return;
                    }
                    checkUniquenessOnBlur(
                        'phone',
                        'phoneData',
                        getPhoneData()?.phoneNumber,
                        getPhoneCompareValue(),
                        buildPhoneCompareValue(accountDeliveryDriver?.account?.countryCode, accountDeliveryDriver?.account?.phone),
                    );
                }}
            >
            <Form.Item
                label={t('delivery.personnel.label.phone')}
                name="phoneData"
                rules={[
                    { required: true, message: t('delivery.personnel.message.phoneRequired') },
                    {
                        validator: (_, value: CountryCodeAndPhoneNumber) => {
                            if (!value || !value.phoneNumber) {
                                return Promise.reject(new Error(t('delivery.personnel.message.phoneRequired')));
                            }
                            // 根据国家编码验证位数
                            if (value.countryCode === 1) {
                                // +1 (美国): 10位数字
                                if (value.phoneNumber.length !== 10) {
                                    return Promise.reject(new Error(t('delivery.personnel.message.invalidPhoneNumberLength')));
                                }
                            } else if (value.countryCode === 86) {
                                // +86 (中国): 11位数字
                                if (value.phoneNumber.length !== 11) {
                                    return Promise.reject(new Error(t('delivery.personnel.message.invalidPhoneNumberLength')));
                                }
                            }
                            return Promise.resolve();
                        }
                    }
                ]}
            >
                <PhoneInput disabled={isDisabled} readOnly={isDisabled} />
            </Form.Item>
            </div>

            <Form.Item label={t('delivery.personnel.label.password')} name={['account', 'password']} validateTrigger="onBlur" rules={
                mode === 'create'
                    ? [{ required: true, message: t('delivery.personnel.message.passwordRequired') }, passwordPolicyRule(t('accountSecurity.passwordNotMeet') as string)]
                    : [passwordPolicyRule(t('accountSecurity.passwordNotMeet') as string)]
            }>
                <Input.Password placeholder={t('accountSecurity.passwordHint') as string} disabled={isDisabled} />
            </Form.Item>

            <Form.Item 
                label={t('delivery.personnel.label.name')} 
                name={['account', 'name']} 
                rules={[{ required: isRequired, message: t('delivery.personnel.message.nameRequired') }]}
            >
                <Input placeholder={t('delivery.personnel.label.namePlaceholder')} disabled={isDisabled} />
            </Form.Item>

            <Form.Item
                label={t('delivery.personnel.label.email')}
                name={['account', 'email']}
                rules={[{ type: 'email', message: t('delivery.personnel.message.emailInvalid')}]}
            >
                <Input placeholder={t('delivery.personnel.label.emailPlaceholder')} disabled={isDisabled} />
            </Form.Item>

            {
                mode === 'edit' && (
                    <Form.Item label={t('delivery.personnel.label.creationDate')} name="creationDate">
                        <Input readOnly disabled={isDisabled} />
                    </Form.Item>
                )
            }

            {/*<Form.Item label={t('delivery.personnel.label.personnelStatus')} name={['account', 'status']}>*/}
            {/*    <Select*/}
            {/*        options={[{*/}
            {/*            label: t('delivery.personnel.label.active'),*/}
            {/*            value: 1,*/}
            {/*        }, {*/}
            {/*            label: t('delivery.personnel.label.inactive'),*/}
            {/*            value: 0,*/}
            {/*        }]}*/}
            {/*        onChange={(value) => handleChange('status', value)}*/}
            {/*        disabled={isDisabled}*/}
            {/*    />*/}
            {/*</Form.Item>*/}

            <Form.Item label={t('delivery.personnel.label.isEmployed')} name={['account', 'isEmployed']}>
                <Select
                    options={[{
                        label: t('delivery.personnel.label.employed'),
                        value: true,
                    }, {
                        label: t('delivery.personnel.label.unemployed'),
                        value: false,
                    }]}
                    onChange={(value) => handleChange('isEmployed', value)}
                    disabled={mode === 'create' || isDisabled}
                />
            </Form.Item>
            <Divider />

            { showDspSelector && (
                <Form.Item
                    label={t('delivery.personnel.label.company')}
                    name="dspId"
                    rules={[{ required: true, message: t('delivery.personnel.message.pleaseSelectCompany')}]}
                >
                    <Select
                        options={dspOptions}
                        placeholder={t('delivery.personnel.message.pleaseSelectCompany')}
                        loading={dspLoading}
                        onChange={(value) => handleChange('dspId', value)}
                        disabled={mode === 'edit'} // 编辑模式下禁用选择
                        showSearch
                        filterOption={(input, option) =>
                            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </Form.Item>
            )}

            <Form.Item label={t('delivery.personnel.label.dutyStatus')} name="isOnDuty" hidden>
                <Select
                    options={[{
                        label: t('delivery.personnel.label.onDuty'),
                        value: true,
                    }, {
                        label: t('delivery.personnel.label.offDuty'),
                        value: false,
                    }]}
                    onChange={(value) => handleChange('isOnDuty', value)}
                    disabled={isDisabled}
                />
            </Form.Item>
            <Form.Item
                label={t('delivery.driver.idDocumentType')}
                name="idDocumentType"
                rules={[{ required: true, message: t('delivery.driver.idDocumentTypeRequired') }]}
                initialValue={0}
            >
                <Select
                    placeholder={t('delivery.driver.selectIdDocumentType')}
                    options={[
                        { label: t('delivery.driver.driverLicense'), value: 0 },
                        // { label: t('delivery.driver.idCard'), value: 1 },
                        // { label: t('delivery.driver.passport'), value: 2 },
                        // { label: t('delivery.driver.ead'), value: 3 },
                    ]}
                    onChange={(value) => handleChange('idDocumentType', value)}
                    disabled={true}
                />
            </Form.Item>
            <Form.Item
                label={t('delivery.driver.idDocumentNumber')}
                name="idDocumentNumber"
                rules={isRequired ? [
                    { required: true, message: t('delivery.driver.idDocumentNumberRequired') },
                    {
                        validator: (_, value) => {
                            if (!value || typeof value !== 'string') {
                                return Promise.resolve();
                            }
                            // 验证只能包含大写字母和数字
                            const validPattern = /^[A-Z0-9]+$/;
                            if (!validPattern.test(value)) {
                                return Promise.reject(new Error(t('delivery.driver.idDocumentNumberInvalid')));
                            }
                            return Promise.resolve();
                        }
                    }
                ] : []}
            >
                <Input
                    placeholder={t('delivery.driver.inputIdDocumentNumber')}
                    disabled={isDisabled}
                    onFocus={() => { focusValueRef.current.idDocumentNumber = getIdDocumentNumber(); }}
                    onBlur={() => checkUniquenessOnBlur(
                        'idDocumentNumber', 'idDocumentNumber', getIdDocumentNumber(), getIdDocumentNumber(), accountDeliveryDriver?.idDocumentNumber
                    )}
                />
            </Form.Item>
            <Form.Item
                label={t('delivery.driver.idDocumentPhotos')}
                name="idDocumentPhotos"
                rules={isRequired ? [
                    { required: true, message: t('delivery.driver.idDocumentPhotosRequired') },
                    { type: 'array', min: 1, message: t('delivery.driver.idDocumentPhotosMin') },
                    { type: 'array', max: 6, message: t('delivery.driver.idDocumentPhotosMax') }
                ] : []}
            >
                <ImageUploader
                    value={accountDeliveryDriver?.idDocumentPhotos || []}
                    displayUrls={accountDeliveryDriver?.idDocumentImageUrls || []}
                    onChange={(urls: string[]) => form.setFieldsValue({ idDocumentPhotos: urls })}
                    maxCount={6}
                    disabled={isDisabled}
                />
            </Form.Item>
            <Form.Item 
                label={t('delivery.personnel.label.vehicleType')} 
                name="vehicleTypeId" 
                rules={[{ required: isRequired, message: t('delivery.personnel.message.vehicleTypeRequired') }]}
            >
                <Select
                    options={vehicleTypeOptions}
                    placeholder={t('delivery.personnel.message.vehicleTypeRequired') as string}
                    onChange={(value) => handleChange('vehicleType', value)}
                    disabled={isDisabled}
                />
            </Form.Item>
            <Form.Item
                label={t('delivery.personnel.label.vehiclePlate')}
                name="vehiclePlate"
                rules={[{ required: isRequired, message: t('delivery.personnel.message.vehiclePlateRequired') }]}
            >
                <Input placeholder={t('delivery.personnel.label.vehiclePlatePlaceholder')} disabled={isDisabled} />
            </Form.Item>
            <Form.Item 
                label={t('delivery.personnel.label.vehiclePhotos')} 
                name="vehiclePhotos"
                rules={[{required: isRequired, message: t('delivery.personnel.message.pleaseUploadVehiclePhotos')}]}
            >
                <ImageUploader
                    value={accountDeliveryDriver?.vehiclePhotos || []}
                    displayUrls={accountDeliveryDriver?.vehicleImageUrls || []}
                    onChange={(urls: string[]) => form.setFieldsValue({ vehiclePhotos: urls })}
                    maxCount={1}
                    disabled={isDisabled}
                />
            </Form.Item>
            {mode === 'edit' && (
                <>
                    <Divider />
                    <Form.Item label={t('delivery.driver.verificationStatus')} style={{ marginBottom: 0 }}>
                        <Typography.Text>
                            {getVerificationStatusText(accountDeliveryDriver?.verificationStatus)}
                        </Typography.Text>
                    </Form.Item>
                    {accountDeliveryDriver?.verificationStatus === 3 && accountDeliveryDriver?.reviewComment && (
                        <Form.Item label={t('delivery.driver.failureReason')} style={{ marginBottom: 0 }}>
                            <Typography.Text>
                                {accountDeliveryDriver.reviewComment}
                            </Typography.Text>
                        </Form.Item>
                    )}
                    <Form.Item wrapperCol={{ offset: 6, span: 18 }}>
                        <Alert
                            message={t('delivery.driver.verificationStatusWarning')}
                            type="warning"
                            showIcon
                        />
                    </Form.Item>
                </>
            )}
        </Form>
    )
};

// 获取审核状态显示文本
const getVerificationStatusText = (status: number | undefined): string => {
    if (status === undefined || status === null) {
        return t('delivery.driver.verificationStatusNotReady');
    }
    switch (status) {
        case 0:
            return t('delivery.driver.verificationStatusNotReady');
        case 1:
            return t('delivery.driver.verificationStatusPending');
        case 2:
            return t('delivery.driver.verificationStatusApproved');
        case 3:
            return t('delivery.driver.verificationStatusRejected');
        default:
            return t('delivery.driver.verificationStatusNotReady');
    }
};


export interface AccountDeliveryDriverFormDrawerProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: (values: AccountDeliveryDriverType) => Promise<void>;
    accountDeliveryDriver?: AccountDeliveryDriverType | null;
    mode: 'create' | 'edit'; // Explicitly specify mode
    source?: AccountsPageMode; // 页面入口：central/wms/dsp，透传给表单决定 Company/DSP 选择框
    dspOptions?: Array<{ label: string; value: string; dspCode: string }>;
    dspLoading?: boolean;
    onUniquenessConflict?: (conflict: DriverConflict) => void;
}

export const AccountDeliveryDriverFormDrawer: React.FC<AccountDeliveryDriverFormDrawerProps> = ({
    visible,
    onClose,
    onSubmit,
    accountDeliveryDriver,
    mode,
    source,
    dspOptions = [],
    dspLoading = false,
    onUniquenessConflict
}) => {
    const drawerTitle = mode === 'create' ? t('delivery.personnel.label.newPersonnel') : t('delivery.personnel.label.personnelDetails');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resignationConfirmVisible, setResignationConfirmVisible] = useState(false);
    const [pendingSubmitValues, setPendingSubmitValues] = useState<AccountDeliveryDriverType | null>(null);
    const [form] = Form.useForm<AccountDeliveryDriverType>();
    const [uniquenessCheckGuard] = useState(createUniquenessCheckGuard);
    const pointerReleaseWaiterRef = useRef<ReturnType<typeof createPointerReleaseWaiter> | null>(null);
    useEffect(() => {
        const waiter = createPointerReleaseWaiter();
        pointerReleaseWaiterRef.current = waiter;
        return () => {
            waiter.dispose();
            pointerReleaseWaiterRef.current = null;
        };
    }, []);
    const waitForPointerRelease = () => pointerReleaseWaiterRef.current?.wait() ?? Promise.resolve();

    // 点取消 / 关闭 / 遮罩时在点击处理中同步作废失焦查询，保证早于失焦校验等待结束
    const handleClose = () => {
        uniquenessCheckGuard.invalidateAll();
        onClose();
    };

    // 抽屉被关闭（含提交成功等非 handleClose 途径）后，未返回的失焦查询结果作废，不再弹窗
    useEffect(() => {
        if (!visible) {
            uniquenessCheckGuard.invalidateAll();
        }
    }, [visible, uniquenessCheckGuard]);

    // 检查原始司机信息是否为离职状态
    const isResigned = mode === 'edit' && (accountDeliveryDriver?.isEmployed === false || accountDeliveryDriver?.account?.isEmployed === false);
    // 检查原始司机信息是否为封禁状态
    const isBlacklisted = mode === 'edit' && accountDeliveryDriver?.isBlacklist === true;

    const handleSubmit = async () => {
        // 提交以后端校验结果为准，作废此前未返回的失焦查询，避免同一冲突弹两次
        uniquenessCheckGuard.invalidateAll();
        try {
            setIsSubmitting(true);
            const values: any = await form.validateFields();
            // 将 phoneData 对象转换为 account.countryCode 和 account.phone
            if (values.phoneData && typeof values.phoneData === 'object' && 'countryCode' in values.phoneData && 'phoneNumber' in values.phoneData) {
                const phoneData = values.phoneData as CountryCodeAndPhoneNumber;
                values.account = {
                    ...values.account,
                    countryCode: phoneData.countryCode,
                    phone: phoneData.phoneNumber
                };
                delete values.phoneData;
            }

            // 编辑模式下，检查是否选中了离职
            if (mode === 'edit') {
                const newIsEmployed = values.account.isEmployed ?? true;

                if (newIsEmployed === false) {
                    // 选中了离职，需要确认
                    setPendingSubmitValues(values);
                    setResignationConfirmVisible(true);
                    setIsSubmitting(false);
                    return;
                }
            }

            await onSubmit(values);
        } catch (error) {
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleResignationConfirm = async () => {
        if (pendingSubmitValues) {
            try {
                setIsSubmitting(true);
                await onSubmit(pendingSubmitValues);
                setResignationConfirmVisible(false);
                setPendingSubmitValues(null);
            } catch (error) {
                console.error(error);
            } finally {
                setIsSubmitting(false);
            }
        }
    }

    const handleResignationCancel = () => {
        setResignationConfirmVisible(false);
        setPendingSubmitValues(null);
    }

    useEffect(() => {
        form.resetFields();
        if (accountDeliveryDriver) {
            const formValues: any = {
                ...accountDeliveryDriver,
                vehiclePhotos: accountDeliveryDriver.vehicleImageS3KeysJson || [],
                idDocumentPhotos: accountDeliveryDriver.idDocumentImageS3KeysJson || [],
                creationDate: formatDate(accountDeliveryDriver.createdAt as string),
                idDocumentType: 0, // 默认选中驾照类型
                // 将 account.countryCode 和 account.phone 转换为 phoneData 对象
                phoneData: accountDeliveryDriver.account ? {
                    countryCode: accountDeliveryDriver.account.countryCode,
                    phoneNumber: accountDeliveryDriver.account.phone
                } : {
                    countryCode: countryCodeOptions[0].value,
                    phoneNumber: ''
                },
                account: {
                    ...accountDeliveryDriver.account,
                    isEmployed: accountDeliveryDriver.isEmployed ?? accountDeliveryDriver.account.isEmployed ?? true, // 优先使用顶层的 isEmployed，确保 isEmployed 被正确设置
                }
            };
            form.setFieldsValue(formValues);
        } else {
            form.setFieldsValue({
                account: {
                    name: '',
                    email: '',
                    status: 1,
                    isEmployed: true, // 新建时默认在职
                },
                phoneData: {
                    countryCode: countryCodeOptions[0].value,
                    phoneNumber: ''
                } as CountryCodeAndPhoneNumber,
                isOnDuty: true,
                idDocumentType: 0, // 默认选中驾照类型
            } as any);
        }
    }, [accountDeliveryDriver, form, visible]);

    // 监听 dspOptions 变化，确保编辑模式下能正确显示 DSP 名称
    useEffect(() => {
        if (mode === 'edit' && accountDeliveryDriver && dspOptions.length > 0) {
            // 强制重新渲染表单，确保 DSP 名称能正确显示
            const currentDspId = form.getFieldValue('dspId');
            if (currentDspId) {
                form.setFieldsValue({ dspId: currentDspId });
            }
        }
    }, [dspOptions, mode, accountDeliveryDriver, form]);


    return (
        <EditableDrawer
            title={drawerTitle}
            visible={visible}
            onClose={handleClose}
            onSubmit={isResigned || isBlacklisted ? undefined : handleSubmit}
            confirmText={isSubmitting ? t('delivery.personnel.label.processing') : t('components.button.confirm')}
            confirmDisabled={isSubmitting}
            showDefaultFooter={!isResigned && !isBlacklisted}
            width={600}
        >
            <AccountDeliveryDriverForm
                mode={mode}
                form={form}
                accountDeliveryDriver={accountDeliveryDriver}
                source={source}
                dspOptions={dspOptions}
                dspLoading={dspLoading}
                onUniquenessConflict={onUniquenessConflict}
                uniquenessCheckGuard={uniquenessCheckGuard}
                waitForPointerRelease={waitForPointerRelease}
            />

            <Modal
                open={resignationConfirmVisible}
                title={t('delivery.personnel.message.resignationConfirmTitle')}
                okText={t('components.button.confirm')}
                cancelText={t('components.button.cancel')}
                onOk={handleResignationConfirm}
                onCancel={handleResignationCancel}
                confirmLoading={isSubmitting}
            >
                <p>{t('delivery.personnel.message.resignationConfirmContent')}</p>
            </Modal>
        </EditableDrawer>
    );
};